import { useState, useRef } from 'react';
import type { Question, Option, QuestionType, RichText, SliderParams } from './types';
import { isNonAnswerType } from './types';
import QuestionMenu from './QuestionMenu';
import AddQuestionModal from './AddQuestionModal';
import DisplayLogicModal from './DisplayLogicModal';
import AdvancedModal from './AdvancedModal';
import RichTextEditor from './RichTextEditor';

type Props = {
  question: Question;
  index: number;
  allQuestions: Question[];
  onChange: (patch: Partial<Question>) => void;
  onRemove: () => void;
  onInsertBelow: (type: QuestionType) => void;
  /** 0901 新增：分页栏当前页码（编辑端从 1 开始计数；undefined 时退化为原题号） */
  pageNumber?: number;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent, index: number) => void;
  onDragOver?: (e: React.DragEvent, index: number) => void;
  onDragEnd?: () => void;
  onDrop?: (e: React.DragEvent, index: number) => void;
  isDragOver?: boolean;
};

/**
 * 单题（截图 2 设计）：
 *   [N → ] 题干 input
 *           问题描述（可选）
 *           输入预览区（圆角输入框 / 圆角单选卡片组 / 滑动条）
 *           [必填]                              [⋮]
 */
export default function QuestionCard({
  question,
  index,
  allQuestions,
  onChange,
  onRemove,
  onInsertBelow,
  pageNumber,
  draggable = false,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDrop,
  isDragOver,
}: Props) {
  const q = question;
  const [addOpen, setAddOpen] = useState(false);
  const [logicOpen, setLogicOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const options: Option[] = (q.options ?? []).map((o) =>
    typeof o === 'string' ? { value: o } : o,
  );

  const setTitle = (rich: RichText) => onChange({ title: rich });
  const setDescription = (v: string) => onChange({ description: v });
  const updateOption = (i: number, patch: Partial<Option>) => {
    const next = [...options];
    next[i] = { ...next[i], ...patch };
    onChange({ options: next });
  };
  const addOption = () =>
    onChange({ options: [...options, { value: `选项${options.length + 1}` }] });
  const removeOption = (i: number) =>
    onChange({ options: options.filter((_, idx) => idx !== i) });

  const conditionsCount = q.displayLogic?.conditions?.length ?? 0;
  const isStatement = q.type === 'statement';
  const isPagebreak = q.type === 'pagebreak';
  const nonAnswer = isNonAnswerType(q.type);

  return (
    <div
      className={`q-item${isDragOver ? ' is-drag-over' : ''}${isStatement ? ' is-statement' : ''}${isPagebreak ? ' is-pagebreak' : ''}`}
      draggable={draggable}
      onDragStart={(e) => {
        // 排除输入控件内的拖拽
        const target = e.target as HTMLElement;
        if (target.closest('input, textarea, button, .opt-del, .q-menu, .opt-image-cell, .q-title-image, .rich-text-input, .rich-text-toolbar')) {
          e.preventDefault();
          return;
        }
        onDragStart?.(e, index);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver?.(e, index);
      }}
      onDrop={(e) => {
        e.preventDefault();
        onDrop?.(e, index);
      }}
      onDragEnd={() => onDragEnd?.()}
    >
      {!isPagebreak && (
        <div className="q-item-side">
          <span className="q-item-drag" title="拖动排序" aria-label="拖动排序">
            <IconDrag />
          </span>
          <span className="q-item-no">{isStatement ? '★' : index + 1}</span>
          <span className="q-item-arrow">→</span>
        </div>
      )}

      <div className="q-item-main">
        {isPagebreak ? (
          <div className="q-pagebreak-marker">
            <span className="q-pagebreak-line" />
            <span className="q-pagebreak-label">{`第 ${pageNumber ?? index + 1} 页`}</span>
            <span className="q-pagebreak-line" />
          </div>
        ) : (
          <RichTextEditor
            value={q.title}
            onChange={setTitle}
            placeholder={isStatement ? '请输入标题说明的主标题' : '请输入题干'}
            inputClassName="q-title-input"
            ariaLabel="题目主标题"
          />
        )}
        {!isPagebreak && (
          <input
            className="q-desc-input"
            value={q.description ?? ''}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={isStatement ? '副标题（可选）' : '问题描述（可选）'}
          />
        )}

        {!isPagebreak && !isStatement && <TitleImageCell image={q.image} onChange={(image) => onChange({ image })} />}

        {!isPagebreak && (
        <div className="q-input-area">
          {q.type === 'text' && (
            <div className="q-input-box">待填写者输入</div>
          )}
          {q.type === 'longtext' && (
            <textarea
              className="q-input-box is-textarea"
              placeholder="待填写者输入"
              disabled
              rows={3}
            />
          )}

          {(q.type === 'single' || q.type === 'multi' || q.type === 'select') && (
            <>
              <div className="q-radio-cards">
                {options.map((o, i) => (
                  <div key={i} className="q-radio-card">
                    <span className="dot" />
                    <input
                      className="opt-edit"
                      value={o.value}
                      onChange={(e) => updateOption(i, { value: e.target.value })}
                      placeholder={`选项${i + 1}`}
                    />
                    <input
                      className="input"
                      type="number"
                      step="any"
                      style={{ width: 74, flex: 'none', padding: '4px 8px', fontSize: 13 }}
                      title="该选项分值（评分题用，留空不计分）"
                      placeholder="分值"
                      value={o.score ?? ''}
                      onChange={(e) =>
                        updateOption(i, { score: e.target.value === '' ? undefined : Number(e.target.value) })
                      }
                    />
                    <OptionImageCell
                      image={o.image}
                      onChange={(image) => updateOption(i, { image })}
                    />
                    <button
                      type="button"
                      className="opt-del"
                      onClick={() => removeOption(i)}
                      disabled={options.length <= 1}
                      title="删除选项"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" className="q-add-opt" onClick={addOption}>
                + 添加选项
              </button>
            </>
          )}

          {q.type === 'slider' && (
            <SliderEditor
              params={q.params as SliderParams | undefined}
              onChange={(next) => onChange({ params: { ...(q.params ?? {}), ...next } })}
            />
          )}

          {q.type === 'matrix' && (
            <MatrixEditor
              rows={q.params?.rows ?? []}
              columns={q.params?.columns ?? []}
              columnScores={q.params?.columnScores ?? []}
              onChange={(rows, columns, columnScores) =>
                onChange({ params: { ...(q.params ?? {}), rows, columns, columnScores } })
              }
            />
          )}

          {q.type === 'file' && (
            <div className="q-input-box">填写者可上传 1 个附件（图片自动压缩 / PDF ≤ 3MB）</div>
          )}

          {isStatement && (
            <div className="q-statement-hint">
              <span>标题说明</span>
              <em>填写者无需作答，仅作页面说明 / 章节分隔</em>
            </div>
          )}
        </div>
        )}

        {!isPagebreak && (
        <div className="q-actions">
          {!nonAnswer && (
            <button
              type="button"
              className={`q-chip ${q.required ? 'is-on' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange({ required: !q.required });
              }}
              aria-pressed={q.required}
            >
              必填
            </button>
          )}

          {!nonAnswer && (
            <button
              type="button"
              className={`q-chip ${q.onPreface ? 'is-on' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange({ onPreface: !q.onPreface });
              }}
              aria-pressed={q.onPreface}
              title="在前置页（协议/须知页）随基本信息一起填写"
            >
              前置页
            </button>
          )}

          <QuestionMenu
            logicCount={conditionsCount}
            onShowLogic={() => setLogicOpen(true)}
            onShowAdvanced={() => setAdvancedOpen(true)}
            onInsertBelow={() => setAddOpen(true)}
            onInsertStatementBelow={() => onInsertBelow('statement')}
            onInsertPagebreakBelow={() => onInsertBelow('pagebreak')}
            onRemove={onRemove}
          />
        </div>
        )}

        {isPagebreak && (
          <div className="q-actions is-pagebreak-actions">
            <span className="hint">填写端将在此处切换到下一页</span>
            <button
              type="button"
              className="opt-del"
              onClick={onRemove}
              title="删除分页栏"
            >
              ✕
            </button>
          </div>
        )}
      </div>

      {logicOpen && (
        <DisplayLogicModal
          logic={q.displayLogic ?? { operator: 'AND', conditions: [] }}
          allQuestions={allQuestions}
          currentQid={q.id}
          onChange={(logic) => onChange({ displayLogic: logic })}
          onClose={() => setLogicOpen(false)}
        />
      )}

      {advancedOpen && (
        <AdvancedModal
          question={q}
          options={options}
          onChange={(patch) => onChange(patch)}
          onChangeOption={(i, patch) => {
            const next = [...options];
            next[i] = { ...next[i], ...patch };
            onChange({ options: next });
          }}
          onClose={() => setAdvancedOpen(false)}
        />
      )}

      {addOpen && (
        <AddQuestionModal
          onSelect={(t) => {
            setAddOpen(false);
            onInsertBelow(t);
          }}
          onClose={() => setAddOpen(false)}
        />
      )}
    </div>
  );
}

/** 题干配图：大图自动等比缩到 1280px 内转 JPEG，避免撑爆问卷配置 */
function TitleImageCell({
  image,
  onChange,
}: {
  image?: string;
  onChange: (image: string | undefined) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const handleFile = (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      alert('图片不能超过 10MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      if (file.size <= 300 * 1024) {
        onChange(dataUrl);
        return;
      }
      const img = new Image();
      img.onload = () => {
        const max = 1280;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        const ctx = canvas.getContext('2d')!;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        onChange(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.onerror = () => alert('图片读取失败，请换一张试试');
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  };
  return (
    <div className="q-title-image" style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '2px 0 6px' }}>
      {image ? (
        <>
          <img src={image} alt="题干配图" style={{ maxHeight: 72, maxWidth: 180, borderRadius: 6, border: '1px solid var(--border, #e5e7eb)' }} />
          <button type="button" className="opt-del" onClick={() => onChange(undefined)} title="移除题干配图">✕</button>
        </>
      ) : (
        <button type="button" className="opt-image-add" onClick={() => inputRef.current?.click()} title="为题干添加图片">
          <IconImage /> 题干配图
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
          e.target.value = '';
        }}
      />
    </div>
  );
}

/** 矩阵量表的行/列编辑：行为项目列表；列为量表刻度（可配分值，用于评分统计） */
function MatrixEditor({
  rows,
  columns,
  columnScores,
  onChange,
}: {
  rows: string[];
  columns: string[];
  columnScores: (number | null)[];
  onChange: (rows: string[], columns: string[], columnScores: (number | null)[]) => void;
}) {
  const scores = columns.map((_, i) => (columnScores[i] === undefined ? null : columnScores[i]));
  const editStr = (list: string[], i: number, v: string) => list.map((x, idx) => (idx === i ? v : x));
  return (
    <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 180 }}>
        <div className="qe-label" style={{ marginBottom: 6 }}>矩阵行（评价项目）</div>
        {rows.map((v, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <input className="opt-edit" value={v} onChange={(e) => onChange(editStr(rows, i, e.target.value), columns, scores)} placeholder={`项目${i + 1}`} />
            <button type="button" className="opt-del" onClick={() => onChange(rows.filter((_, idx) => idx !== i), columns, scores)} disabled={rows.length <= 1} title="删除项目">✕</button>
          </div>
        ))}
        <button type="button" className="q-add-opt" onClick={() => onChange([...rows, `项目${rows.length + 1}`], columns, scores)}>+ 添加项目</button>
      </div>
      <div style={{ flex: 1.2, minWidth: 230 }}>
        <div className="qe-label" style={{ marginBottom: 6 }}>矩阵列（量表刻度 · 分值可选）</div>
        {columns.map((v, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <input className="opt-edit" value={v} onChange={(e) => onChange(rows, editStr(columns, i, e.target.value), scores)} placeholder={`刻度${i + 1}`} />
            <input
              className="input"
              type="number"
              step="any"
              style={{ width: 70, flex: 'none', padding: '4px 8px', fontSize: 13 }}
              title="该刻度分值（留空不计分）"
              placeholder="分值"
              value={scores[i] ?? ''}
              onChange={(e) => onChange(rows, columns, scores.map((s, idx) => (idx === i ? (e.target.value === '' ? null : Number(e.target.value)) : s)))}
            />
            <button
              type="button"
              className="opt-del"
              onClick={() => onChange(rows, columns.filter((_, idx) => idx !== i), scores.filter((_, idx) => idx !== i))}
              disabled={columns.length <= 1}
              title="删除刻度"
            >
              ✕
            </button>
          </div>
        ))}
        <button type="button" className="q-add-opt" onClick={() => onChange(rows, [...columns, `刻度${columns.length + 1}`], [...scores, null])}>+ 添加刻度</button>
      </div>
    </div>
  );
}

/** 滑动条设置：最小值/最大值/数值间隔/不允许小数 + 两端标签文本 */
function SliderEditor({
  params,
  onChange,
}: {
  params?: SliderParams;
  onChange: (next: Partial<SliderParams>) => void;
}) {
  const min = params?.min ?? 0;
  const max = params?.max ?? 100;
  const step = params?.step ?? 1;
  const allowDecimal = !!params?.allowDecimal;
  const minLabel = params?.minLabel ?? '';
  const maxLabel = params?.maxLabel ?? '';
  const unit = params?.unit ?? '';
  // 实际区间长度（小数位数提示）
  const decimals = useDecimals(step);

  return (
    <div className="q-slider-editor">
      <div className="q-slider-preview">
        <div className="q-slider-preview-top">
          <span className="q-slider-preview-label">{minLabel || (allowDecimal ? min : min)}</span>
          <input
            type="range"
            min={min}
            max={max}
            step={step}
            defaultValue={(min + max) / 2}
            disabled
          />
          <span className="q-slider-preview-label">{maxLabel || max}</span>
        </div>
        <div className="q-slider-preview-bottom">
          {Array.from({ length: 6 }).map((_, i) => {
            const span = max - min;
            const v = min + (span * i) / 5;
            const snap = allowDecimal ? Number(v.toFixed(decimals)) : Math.round(v);
            return <span key={i}>{snap}</span>;
          })}
        </div>
      </div>

      <div className="q-slider-fields">
        <label className="qe-line">
          最小值
          <input
            type="number"
            step={allowDecimal ? 'any' : 1}
            className="input"
            value={min}
            onChange={(e) => {
              const v = e.target.value === '' ? 0 : Number(e.target.value);
              onChange({ min: v });
            }}
          />
        </label>
        <label className="qe-line">
          最大值
          <input
            type="number"
            step={allowDecimal ? 'any' : 1}
            className="input"
            value={max}
            onChange={(e) => {
              const v = e.target.value === '' ? 0 : Number(e.target.value);
              onChange({ max: v });
            }}
          />
        </label>
        <label className="qe-line">
          数值间隔
          <input
            type="number"
            step={allowDecimal ? 'any' : 1}
            min={allowDecimal ? 0.0001 : 1}
            className="input"
            value={step}
            onChange={(e) => {
              const v = e.target.value === '' ? 1 : Number(e.target.value);
              if (!allowDecimal && (!Number.isInteger(v) || v < 1)) {
                alert('不允许小数时数值间隔必须是 ≥1 的整数');
                return;
              }
              onChange({ step: v });
            }}
          />
        </label>
        <label className="qe-line qe-checkbox">
          <input
            type="checkbox"
            checked={!allowDecimal}
            onChange={(e) => {
              const disallow = e.target.checked;
              if (disallow) {
                // 切回"不允许小数"时把 step 规整到 ≥1 整数
                const snapped = Math.max(1, Math.round(step));
                onChange({ allowDecimal: false, step: snapped });
              } else {
                onChange({ allowDecimal: true });
              }
            }}
          />
          不允许小数
        </label>
        <label className="qe-line">
          最小值显示文本
          <input
            type="text"
            className="input"
            maxLength={24}
            value={minLabel}
            placeholder="如：不满意（1）"
            onChange={(e) => onChange({ minLabel: e.target.value })}
          />
        </label>
        <label className="qe-line">
          最大值显示文本
          <input
            type="text"
            className="input"
            maxLength={24}
            value={maxLabel}
            placeholder="如：满意（15）"
            onChange={(e) => onChange({ maxLabel: e.target.value })}
          />
        </label>
        <label className="qe-line">
          单位（可选）
          <input
            type="text"
            className="input"
            maxLength={6}
            value={unit}
            placeholder="如：分、℃"
            onChange={(e) => onChange({ unit: e.target.value })}
          />
        </label>
      </div>
    </div>
  );
}

function useDecimals(step: number): number {
  if (!Number.isFinite(step) || step <= 0) return 0;
  const s = String(step);
  const idx = s.indexOf('.');
  if (idx < 0) return 0;
  return Math.min(4, s.length - idx - 1);
}

function IconDrag() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="9" cy="6" r="1.5" />
      <circle cx="9" cy="12" r="1.5" />
      <circle cx="9" cy="18" r="1.5" />
      <circle cx="15" cy="6" r="1.5" />
      <circle cx="15" cy="12" r="1.5" />
      <circle cx="15" cy="18" r="1.5" />
    </svg>
  );
}

/** 选项的图片上传格：缩略图 / +图片 / ✕ */
function OptionImageCell({
  image,
  onChange,
}: {
  image?: string;
  onChange: (image: string | undefined) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const handleFile = (file: File) => {
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) {
      alert('图片不能超过 1.5MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result as string);
    reader.readAsDataURL(file);
  };
  return (
    <div className="opt-image-cell">
      {image ? (
        <>
          <img src={image} alt="选项图" className="opt-thumb" />
          <button
            type="button"
            className="opt-image-del"
            onClick={() => onChange(undefined)}
            title="移除图片"
          >
            ✕
          </button>
        </>
      ) : (
        <button
          type="button"
          className="opt-image-add"
          onClick={() => inputRef.current?.click()}
          title="添加图片"
        >
          <IconImage /> 图片
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
          e.target.value = ''; // 允许重复选同一张
        }}
      />
    </div>
  );
}

function IconImage() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="M21 15l-5-5L5 21" />
    </svg>
  );
}